import AddNewProfileIcon from '@assets/images/icons-filled/add-new-profile.svg';
import { FeatureFlagTypes } from '@interfaces/organizationFeatureFlag';
import {
  OrganizationUserAccessTypes,
  OrganizationUserEntityRelationTypes,
} from '@interfaces/organizationUser';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import type { SxProps } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Avatar } from '@ovComponents/2-component/avatar/avatar';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Divider } from '@ovComponents/2-component/divider/divider';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { MemberTag } from '@pages/household/components/memberTag';
import { useThemeTokens } from '@providers/themeTokenProvider';
import {
  type ActiveEntityProps,
  UserContext,
} from '@providers/userContextProvider';
import { entityInitials, entityName, isFeatureEnabled } from '@utils/common';
import filterHouseholdsWithAccess from '@utils/filterHouseholdsWithAccess';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { colors, colorsInner } from '../entityMenu/entityMenuColors';
import type { IUserEntity } from './viewAllProfilesDialog.types';

export const ViewAllProfilesDialog = ({
  open,
  onAddNewProfile,
  onViewDetail,
  onCancel,
}: {
  open: boolean;
  onAddNewProfile: () => void;
  onViewDetail: () => void;
  onCancel: () => void;
}) => {
  const navigate = useNavigate();
  const { sys } = useThemeTokens();
  const { t } = useTranslation(['components', 'entityMenu', 'navMenu']);
  const { userContext, activeEntity, setActiveEntity, setActiveHousehold } =
    useContext(UserContext);

  const profileName = useCallback(
    (entity?: ActiveEntityProps) => entityName(entity) || entity?.primaryEmail,
    [],
  );
  const profileInitials = useCallback(
    (entity?: ActiveEntityProps) => entityInitials(entity),
    [],
  );

  const getGroupedEntitiesSortedByName = () => {
    const userEntities: IUserEntity[] =
      (userContext.accessType === OrganizationUserAccessTypes.REP_CODE
        ? userContext?.repCodeEntities
        : userContext?.entities) ?? [];
    const entities = userEntities?.filter((x) => !x.entity.affiliateOnly) ?? [];

    const individualEntities = entities.filter(
      (item) => item.entity.type === 'INDIVIDUAL',
    );
    const nonIndividualEntities = entities.filter(
      (item) => item.entity.type !== 'INDIVIDUAL',
    );

    individualEntities.sort((a, b) =>
      profileName(a.entity).localeCompare(profileName(b.entity)),
    );
    nonIndividualEntities.sort((a, b) =>
      profileName(a.entity).localeCompare(profileName(b.entity)),
    );

    return { individualEntities, nonIndividualEntities };
  };

  const handleEntitySelect = (item: IUserEntity) => {
    const households = filterHouseholdsWithAccess(
      item.entity?.households || [],
      item.entity?.id,
      userContext,
    );
    const isHouseholdDefaultView =
      !!userContext.role?.makeHouseholdDefaultView && households.length > 0;
    if (isHouseholdDefaultView) {
      const household = households[0];
      setActiveHousehold(household);
      navigate(`/households/${household?.id}/clients/${item.entity.id}`);
    }
    setActiveEntity(item.entity);
    onCancel();
  };

  const isAccessGranted =
    userContext.accessType === OrganizationUserAccessTypes.REP_CODE ||
    !!userContext.entities?.some(
      (en) =>
        en.entity?.id === activeEntity?.id &&
        en.relation !== OrganizationUserEntityRelationTypes.OWNER,
    );

  const { individualEntities, nonIndividualEntities } =
    getGroupedEntitiesSortedByName();

  return (
    <Dialog open={open}>
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant='headingLarge'>
          {t('components:viewAllProfilesDialog.switchProfile')}
        </Typography>
        <IconButton onClick={onCancel}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          display='flex'
          alignItems='center'
          flexDirection='row'
          justifyContent='space-between'
          marginBottom={sys.spacing.xxl}
        >
          <Box display='flex' flexDirection='row' alignItems='center'>
            <span style={{ marginRight: sys.spacing.lg }}>
              <Avatar
                state='highlight'
                size='large'
                fgColor={colors[1]}
                bgColor={colorsInner[1]}
              >
                {profileInitials(activeEntity)}
              </Avatar>
            </span>

            <Box sx={{ maxWidth: '400px' }}>
              <Box display='flex' flexDirection='row' alignItems='center'>
                <Typography
                  variant='bodyLarge'
                  weight='bold'
                  mr={sys.spacing.md}
                  sx={profileNameSx}
                >
                  {profileName(activeEntity)}
                </Typography>
                <CheckRoundedIcon style={{ fontSize: '14px' }} />
              </Box>
              {isAccessGranted && (
                <MemberTag
                  sequenceNumber={1}
                  name={t('entityMenu:accessGranted')}
                />
              )}
            </Box>
          </Box>
          <Typography
            variant='bodyMedium'
            onClick={onViewDetail}
            mr={sys.spacing.md}
            sx={{
              cursor: 'pointer',
              display: '-webkit-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical',
            }}
          >
            {t('components:viewAllProfilesDialog.viewDetails')}
          </Typography>
        </Box>
        {individualEntities.length > 0 && (
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {individualEntities.map((item, index) => (
              <ProfileGridItem
                key={item.entity.id}
                item={item}
                index={index}
                onClick={handleEntitySelect}
              />
            ))}
          </Grid>
        )}
        {individualEntities.length > 0 && nonIndividualEntities.length > 0 && (
          <Divider sx={{ marginBottom: 1 }} />
        )}
        {nonIndividualEntities.length > 0 && (
          <>
            <Typography
              variant='bodyMedium'
              fontSize='12px'
              colorVariant='variant'
              sx={{ py: 1 }}
            >
              {t('navMenu:menu.nonIndividualClients').toUpperCase()}
            </Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {nonIndividualEntities.map((item, index) => (
                <ProfileGridItem
                  key={item.entity.id}
                  item={item}
                  index={index}
                  onClick={handleEntitySelect}
                />
              ))}
            </Grid>
          </>
        )}
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          marginTop={sys.spacing.xxl}
        >
          {isFeatureEnabled(FeatureFlagTypes.ALLOW_ADDING_A_NEW_PROFILE) && (
            <Box
              display='flex'
              alignItems='center'
              onClick={onAddNewProfile}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={AddNewProfileIcon}
                alt='view-profile-icon'
                style={{ marginRight: sys.spacing.lg }}
              />
              <Typography variant='bodyMedium'>
                {t('components:viewAllProfilesDialog.addNewEntity')}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const ProfileGridItem = ({
  item,
  index,
  onClick,
}: {
  item: IUserEntity;
  index: number;
  onClick: (item: IUserEntity) => void;
}) => {
  const { sys } = useThemeTokens();
  const { t } = useTranslation(['components', 'entityMenu']);

  const profileName = useCallback(
    (entity?: ActiveEntityProps) => entityName(entity) || entity?.primaryEmail,
    [],
  );
  const profileInitials = useCallback(
    (entity?: ActiveEntityProps) => entityInitials(entity),
    [],
  );

  const isAccessGranted =
    item.relation !== OrganizationUserEntityRelationTypes.OWNER;
  const colorIndex = index % colors.length;

  return (
    <Grid
      key={item.entity.id}
      data-testid={`switch-profile-${index}`}
      item
      xs={6}
    >
      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        paddingLeft={sys.spacing.lg}
        borderRadius={sys.borderRadius.lg}
        onClick={() => onClick(item)}
        sx={{
          cursor: 'pointer',
          height: '48px',
          width: '256px',
          border: `1px solid ${sys.color.outlineVariant}`,
        }}
      >
        <span style={{ marginRight: sys.spacing.lg }}>
          <Avatar
            state='highlight'
            size='large'
            fgColor={colors[colorIndex]}
            bgColor={colorsInner[colorIndex]}
          >
            {profileInitials(item.entity)}
          </Avatar>
        </span>
        <Box sx={{ maxWidth: '200px' }}>
          <Typography variant='bodyMedium' align='left' sx={profileNameSx}>
            {profileName(item.entity)}
          </Typography>
          {isAccessGranted && (
            <MemberTag
              sequenceNumber={colorIndex === 4 ? 6 : colorIndex}
              name={t('entityMenu:accessGranted')}
            />
          )}
        </Box>
      </Box>
    </Grid>
  );
};

const profileNameSx: SxProps = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
};
